import type { ApolloClient, NormalizedCacheObject, ServerError } from '@apollo/client'
import { fromPromise } from '@apollo/client'
import type { NetworkError } from '@apollo/client/errors'
import type { ErrorResponse } from '@apollo/client/link/error'
import type { ServerParseError } from '@apollo/client/link/http/parseAndCheckHttpResponse'
import type { AxiosError } from 'axios'
import type { GraphQLError } from 'graphql'
import Cookies from 'js-cookie'

import type { LutherException } from '@acre/graphql'
import { ErrorProvider, Locales, messagesAlerts, setStorage } from '@acre/utils'

import debug from '../utils/debug/debug'

const errorMessages = messagesAlerts[Locales.GB].alerts

const log = debug('login:authRedirectErrorLink')
//
// Redirects the user to the login screen if they get a 401
//
const authRedirectErrorLink = (function () {
  return ({ response, graphQLErrors, networkError }: ErrorResponse, client: ApolloClient<NormalizedCacheObject>) => {
    const errors = graphQLErrors?.length ? graphQLErrors : ([networkError] as const)
    if (errors.length && errors[0] === undefined) return

    const has401Error = (errors as GraphQLError[] | NetworkError[]).some((error) =>
      isForbiddenError(error, networkError ? 'network' : 'graphql'),
    )
    const useOathKeeper = sessionStorage.getItem('useOathKeeper') !== 'false'
    const initialLocation = window.location.pathname
    const isLoginRoute = !!initialLocation.match(/(?:ory-|client_)?login/) || initialLocation === '/'
    const isSurvey = !!initialLocation.match(/(?:survey)/)

    const isRefreshRoute = initialLocation === '/v1/user/refresh'
    log(
      `useOathKeeper: ${useOathKeeper}, initialLocation: ${initialLocation}, isLoginRoute: ${isLoginRoute}, isRefreshRoute: ${isRefreshRoute}, has401Error: ${has401Error}`,
    )

    if (has401Error && isSurvey) {
      setStorage(sessionStorage, 'authenticated', false.toString()).catch((err) => {
        ErrorProvider.showError(err)
      })
    }

    // We want to avoid showing the signin prompt if the user
    // lands on either the login page or /
    if (has401Error && !isLoginRoute && !isRefreshRoute && !isSurvey) {
      // When authentication fails, clear session and redirect to login
      // The /auth/refresh endpoint no longer exists, so we redirect directly
      log('Authentication failed, redirecting to login')
      const err = new Error(errorMessages.login)
      ErrorProvider.showError(err)
      client.clearStore().then(() => {
        log('Clearing storage and resetting useOathKeeper to ', useOathKeeper)
        sessionStorage.clear()

        setStorage(sessionStorage, 'useOathKeeper', 'true').catch((err) => {
          ErrorProvider.showError(err)
        })

        Cookies.set('login_redirect', location.pathname)

        window.location.href = `${window.location.origin}/ory-login${initialLocation !== '/' ? '?loggedout=true' : ''}`
      })
      return fromPromise(Promise.reject(errors[0]))
    }

    if (has401Error && response) {
      response.errors = []
    }
  }
})()

export default authRedirectErrorLink

export const isForbiddenError = <
  T extends 'graphql' | 'network',
  E extends T extends 'graphql' ? GraphQLError : NetworkError,
>(
  err: E,
  type: T,
) => {
  if (type === ('graphql' as const)) {
    const { extensions, originalError } = err as GraphQLError
    if (extensions.status === 401 || (originalError as LutherException)?.statusCode === 401) {
      log('Error is 401 from GraphQLExtensions')
      return true
    }
    if (originalError && Object.prototype.hasOwnProperty.call(originalError, 'response')) {
      log('Error is AxiosError')
      const { response } = originalError as AxiosError
      return response && response.status === 401
    } else if ((originalError as LutherException)?.statusCode === 401) {
      log('originalError is 401')
      return true
    } else if (originalError && originalError.name === 'LutherException') {
      log('originalError is LutherException')
      const { statusCode } = originalError as LutherException
      return statusCode === 401
    }
  } else if (type === ('network' as const)) {
    log('error is NetworkError')
    if ((err as ServerError).statusCode === 401) {
      log('error is ServerError with 401')
      return true
    }
    if ((err as ServerParseError).statusCode === 401) {
      log('error is ServerParseError with 401')
      return true
    }
  }
  return false
}
